/**
 * Kafka → SSE event stream service.
 * Connects to the API's Server-Sent Events endpoint and transforms
 * Kafka events into EventLogEntry format for the dashboard.
 */

import type { EventLogEntry } from "../types/metrics";

const SSE_URL = `${import.meta.env.VITE_API_URL || "http://localhost:8000"}/api/v1/events/stream`;

/** Map Kafka topic + event type to EventLogEntry type */
function mapEventType(topic: string, event: string): EventLogEntry["type"] {
  const mapping: Record<string, EventLogEntry["type"]> = {
    "security.pii-detected": "pii",
    "security.injection-blocked": "guardrail",
    "guardrails.processed": "guardrail",
    "notes.lifecycle:note.created": "input",
    "notes.lifecycle:note.updated": "storage",
    "notes.lifecycle:note.deleted": "storage",
    "notes.lifecycle:note.summarized": "llm",
    "notes.lifecycle:note.transcribed": "input",
    "notes.lifecycle:note.extracted": "input",
    "auth.events": "input",
  };

  return mapping[`${topic}:${event}`] || mapping[topic] || "input";
}

/** Map Kafka event to severity */
function mapSeverity(topic: string): EventLogEntry["severity"] {
  if (topic === "security.injection-blocked") return "error";
  if (topic === "security.pii-detected") return "warning";
  return "info";
}

/** Generate human-readable message from Kafka event */
function formatMessage(data: Record<string, unknown>): string {
  const event = data.event as string;
  switch (event) {
    case "note.created":
      return `New ${(data.input_type as string)?.toLowerCase() || "text"} note created`;
    case "note.updated":
      return `Note updated (${(data.fields_updated as string[])?.join(", ") || "fields"})`;
    case "note.deleted":
      return "Note deleted";
    case "note.summarized":
      return `Note summarized (${data.summary_length} chars, ${data.tags_count} tags)`;
    case "note.transcribed":
      return `Audio transcribed (${data.transcript_length} chars)`;
    case "note.extracted":
      return `Content extracted from ${data.source_url || "URL"}`;
    case "pii.detected":
      return `PII detected: ${data.entities_masked} entities (${(data.entity_types as string[])?.join(", ")})`;
    case "injection.blocked":
      return `Injection blocked: ${data.injection_type}`;
    case "guardrails.processed":
      return `Guardrails: ${data.intent} (${data.processing_time_ms}ms)`;
    default:
      return event || "Unknown event";
  }
}

let eventCounter = 0;

/** Transform a Kafka SSE event into an EventLogEntry */
export function transformKafkaEvent(
  topic: string,
  data: Record<string, unknown>,
): EventLogEntry {
  eventCounter++;
  const event = (data.event as string) || "";
  // Prefer the server-stamped event_id (uuid4, stamped ONCE in the API at
  // event_bus.publish_lifecycle_event). It is identical across every copy of
  // the event — the direct sse: copy AND the kafka-echo sse: copy — which is
  // exactly what lets us dedup the round-trip (see the seen-set below).
  // Fall back to the synthetic counter only for legacy payloads that predate
  // event_id (JS parallel: like `??` — use the real value, default otherwise).
  const eventId = (data.event_id as string) || `kafka-${eventCounter}`;
  return {
    id: eventId,
    timestamp: data.timestamp
      ? new Date(data.timestamp as number).toISOString()
      : new Date().toISOString(),
    type: mapEventType(topic, event),
    message: formatMessage(data),
    noteId: (data.note_id as string) || undefined,
    severity: mapSeverity(topic),
  };
}

/**
 * Round-trip dedup ("feedback ring" break).
 *
 * The API dual-publishes each event: once on "sse:{topic}" (instant, direct to
 * the browser) and once on "ingress:{topic}" (audit path -> kafka -> back out
 * on "sse:{topic}" as a kafka-echo). The browser therefore sees the SAME event
 * twice, both carrying the SAME server-stamped event_id. Without dedup the
 * stream renders every event twice.
 *
 * WHY a separate Set and not the render buffer:
 * The render buffer in useEventStream is only the last 50 events (maxEvents).
 * The two copies of one event are NOT adjacent — an arbitrary number of OTHER
 * events can arrive between the direct sse: copy and its kafka-echo (the echo
 * pays the full redis -> revbridge -> kafka -> audit -> redis(sse) latency). If
 * we deduped against the 50-item buffer, a burst of >50 events in that window
 * would evict the first copy's id before the echo lands, and the duplicate
 * would slip through. So the seen-set must OUTLIVE the render buffer: we keep
 * the last 500 ids, FIFO-evicted, independent of what the UI is showing.
 *
 * JS note: a Set preserves insertion order, so `seenIds.values().next().value`
 * is the OLDEST id — that is the FIFO entry we evict once we exceed the cap.
 */
const SEEN_ID_CAP = 500;

/**
 * Returns true the first time an id is seen, false on every repeat.
 * Side effect: records the id (and evicts the oldest when over cap).
 */
function markSeen(id: string, seenIds: Set<string>): boolean {
  if (seenIds.has(id)) return false;
  seenIds.add(id);
  if (seenIds.size > SEEN_ID_CAP) {
    // Oldest insertion = first key. Delete it to keep the window bounded.
    const oldest = seenIds.values().next().value;
    if (oldest !== undefined) seenIds.delete(oldest);
  }
  return true;
}

export type EventStreamCallback = (entry: EventLogEntry) => void;
export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";
export type StatusCallback = (status: ConnectionStatus) => void;

/**
 * Create an SSE connection to the Kafka event stream.
 * Returns a cleanup function to close the connection.
 */
export function connectEventStream(
  onEvent: EventStreamCallback,
  onStatus?: StatusCallback,
): () => void {
  onStatus?.("connecting");

  const eventSource = new EventSource(SSE_URL);
  const seenIds = new Set<string>();

  eventSource.onopen = () => {
    onStatus?.("connected");
  };

  eventSource.onerror = () => {
    onStatus?.("error");
    // EventSource auto-reconnects
  };

  // Listen to each Kafka topic
  const topics = [
    "security.pii-detected",
    "security.injection-blocked",
    "auth.events",
    "notes.lifecycle",
    "guardrails.processed",
  ];

  for (const topic of topics) {
    eventSource.addEventListener(topic, (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        const entry = transformKafkaEvent(topic, data);
        // Drop the second copy of a round-tripped event. entry.id is the
        // server event_id when present; the direct sse: copy and the
        // kafka-echo share it, so the echo fails markSeen() and never renders.
        // (Legacy events with no event_id get a unique synthetic id each pass,
        // so they always render — no false dedup.)
        if (!markSeen(entry.id, seenIds)) return;
        onEvent(entry);
      } catch (err) {
        console.warn("Failed to parse SSE event:", err);
      }
    });
  }

  // Heartbeat listener (no-op, just confirms connection is alive)
  eventSource.addEventListener("ping", () => {
    // Connection is alive
  });

  return () => {
    seenIds.clear();
    eventSource.close();
    onStatus?.("disconnected");
  };
}
